import { Divider, Typography, Chip, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { CustomerV2 } from '../../Customer/Model/CustomerModel';
import { customerService } from '../../Customer/CustomerService';
import { Cancel, RestartAlt } from '@mui/icons-material';
import { tenantService } from '../Services/TenantService';
import { Tenant } from '../../Farm/Model/FarmModel';
import { TenantRestartMetaData } from '../Model/Model';
import { globalService } from '../../UIContainer/Global/GlobalService';
import { PortalConfig } from '../../UIContainer/Global/GlobalState';
import PlatformEngineeringConstants from '../Model/CommonConstants';
import { Loader } from '../../../../Common/Components/Loader';
import {
  CardWrapper,
  FooterContainer,
  FooterWrapper,
  PaperWrapper,
} from '../../Common/CommonComponents';
import CustomButton, {
  CustomTextField,
} from '../../../../Common/Components/Controls/InputControls';
import { showToast } from '../../../../lib/toast';

interface Param {
  customerId: string;
}
export default function RestartTenant() {
  const params = useParams<Param>();
  const initialCustomerState: CustomerV2 = {};
  const TenantRestartMetaData: TenantRestartMetaData = {
    ResourceGroupName: '',
    TenantName: '',
    Reason: '',
  };
  const [customerData, setCustomer] = useState(initialCustomerState);
  const initialTenantData: Tenant = {
    Id: '',
    FarmId: 0,
    ApplicationUrl: '',
    StatusID: 0,
    AppDBName: '',
    AppservicePlan: '',
  };
  const [reason, setReason] = useState('');
  const [portalConfig, setPortalConfig] = useState<PortalConfig>();
  const [tenant, setTenant] = useState(initialTenantData);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [tenantStatus, setTenantStatus] = useState('Fetching');
  const [CanCheckStatus, setCanCheckStatus] = useState(false);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReason(event.target.value);
  };

  const loadTenantData = async () => {
    try {
      customerService
        .getCustomer(params.customerId)
        .then((customerData) => setCustomer(customerData));
      globalService.getPortalConfig().then((config) => setPortalConfig(config));
      tenantService
        .GetTenant(params.customerId)
        .then((tenantData) => setTenant(tenantData));
      setLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const GetTenantRunningStatus = async () => {
    try {
      setCanCheckStatus(false);
      setTenantStatus('Fetching');

      const PromiseTimeout = new Promise((resolve) =>
        setTimeout(() => resolve(false), 5000),
      );

      const response = await Promise.race([
        PromiseTimeout,
        tenantService.GetTenantRunningStatus(params.customerId),
      ]);

      if (!response) {
        setTenantStatus('Not Running');
      } else {
        setTenantStatus(response as string);
        setCanCheckStatus(true);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const GetResourceGroup = (
    portalConfig: PortalConfig | undefined,
    FarmId: number,
  ) => {
    return (portalConfig?.Environment === 'Dev' ? 'DevFarm' : 'Farm') + FarmId;
  };

  const handleRestart = () => {
    setCanCheckStatus(false);
    if (reason === '') {
      showToast({
        type: 'error',
        title: PlatformEngineeringConstants.ReasonAlert,
      });
    } else {
      TenantRestartMetaData.TenantName = customerData.Subdomain ?? '';
      TenantRestartMetaData.ResourceGroupName = GetResourceGroup(
        portalConfig,
        tenant.FarmId,
      );
      TenantRestartMetaData.Reason = reason;
      setLoading(true);
      tenantService
        .RestartTenant(params.customerId, TenantRestartMetaData)
        .then(() => {
          setLoading(false);
          showToast({
            type: 'success',
            title: PlatformEngineeringConstants.RestartQueued,
          });
          setCanCheckStatus(true);
        });
    }
  };

  const handleClose = () => {
    history.push('/customer');
  };

  useEffect(() => {
    loadTenantData();
    GetTenantRunningStatus();
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (CanCheckStatus) {
        GetTenantRunningStatus();
      }
    }, 10000);

    return () => clearInterval(intervalId);
  }, [CanCheckStatus]);

  const showStatus = (Status: string) => {
    if (Status === 'Running') {
      return <Chip color="success" label="Running" size="small"></Chip>;
    } else if (Status === 'Fetching') {
      return <Chip color="warning" label="Fetching" size="small"></Chip>;
    } else {
      return <Chip color="error" label="Not Running" size="small"></Chip>;
    }
  };

  return (
    <div>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <PaperWrapper>
          <Typography variant="h5" sx={{ mb: 2 }}>
            Restart Tenant
          </Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={3}>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <CardWrapper>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Tenant ID</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">{customerData.Id}</Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Resource Group</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      {GetResourceGroup(portalConfig, tenant.FarmId)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Customer</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      {customerData.Subdomain}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">Status</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      {showStatus(tenantStatus)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid container spacing={2} alignItems="center" mt={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      Reason for Restarting
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body1">
                      <CustomTextField
                        required={true}
                        id="Reason"
                        label="Reason"
                        variant="outlined"
                        onChange={onChange}
                        value={reason}
                      />
                    </Typography>
                  </Grid>
                </Grid>
              </CardWrapper>
            </Grid>
          </Grid>
          <FooterWrapper>
            <FooterContainer>
              <Grid item xs={12} sm="auto">
                <CustomButton
                  onClick={handleRestart}
                  startIcon={<RestartAlt />}
                  label="Restart"
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <CustomButton
                  onClick={handleClose}
                  startIcon={<Cancel />}
                  label="Back"
                />
              </Grid>
            </FooterContainer>
          </FooterWrapper>
        </PaperWrapper>
      )}
    </div>
  );
}
